import { createPortal } from "react-dom"
import { toast } from "react-toastify"

import { useCreateEnterpriseMutation, useUpdateEnterpriseMutation } from "store/api/enterpriseApi"
import { useAppDispatch } from "store/hooks"
import { setCurrentZip } from "store/tempDataSlise"
import { UITypes } from "types"
import { ReactComponent as IconContractor } from "assets/svg/modal-icon-contractor.svg"

import getErrorMessage from "utils/getErrorMessage"
import { normalizePhoneNumber } from "utils/phoneNumber"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"

import { EnterpriseAccountModalContent } from "./EnterpriseAccountModalContent"

import styles from "./index.module.scss"

const inputValuesInitialState: UITypes.EnterpriseAccount = {
  name: "",
  address1: "",
  address2: "",
  country: [],
  zip: [],
  state: [],
  city: [],
  suburb: [],
  isDisable: false,
  linkedContractors: [],
  email: "",
  url: "",
  fullName: "",
  phone: "",
}

interface Props {
  onCancel: () => void
  onSuccess: () => void
  account?: UITypes.EnterpriseAccount
}

const appendEnterpriseValue = (formData: FormData, name: string, value?: string | number | boolean | null) => {
  if (value === undefined || value === null || value === "") return

  formData.append(name, String(value))
}

const normalizeUrl = (value?: string) => {
  if (!value) return value

  return /^(http:\/\/|https:\/\/)/.test(value) ? value : `https://${value}`
}

export const prepareEnterpriseFormData = (enterprise: UITypes.EnterpriseAccount) => {
  const formData = new FormData()

  appendEnterpriseValue(formData, "name", enterprise.name)
  appendEnterpriseValue(formData, "address1", enterprise.address1)
  appendEnterpriseValue(formData, "address2", enterprise.address2)
  appendEnterpriseValue(formData, "country", enterprise.country?.[0]?.value as string | number | undefined)
  appendEnterpriseValue(formData, "zip", enterprise.zip?.[0]?.value as string | number | undefined)
  appendEnterpriseValue(formData, "city", enterprise.city?.[0]?.value as string | number | undefined)
  appendEnterpriseValue(formData, "suburb", enterprise.suburb?.[0]?.value as string | number | undefined)
  appendEnterpriseValue(formData, "state", enterprise.state?.[0]?.value as string | number | undefined)
  appendEnterpriseValue(formData, "full_name", enterprise.fullName)
  appendEnterpriseValue(formData, "email", enterprise.email)
  appendEnterpriseValue(formData, "tel1", normalizePhoneNumber(enterprise.phone))
  appendEnterpriseValue(formData, "url", normalizeUrl(enterprise.url))
  appendEnterpriseValue(formData, "is_enabled", enterprise.isDisable ? 0 : 1)
  formData.append("contractor_ids", JSON.stringify(enterprise.linkedContractors?.map(item => Number(item.value)) || []))

  if (enterprise.image) {
    formData.append("image", enterprise.image)
  }

  return formData
}

export const EnterpriseAccountModal = ({ onCancel, onSuccess, account = inputValuesInitialState }: Props) => {
  const dispatch = useAppDispatch()
  const [createEnterprise, { isLoading: isCreating }] = useCreateEnterpriseMutation()
  const [updateEnterprise, { isLoading: isUpdating }] = useUpdateEnterpriseMutation()
  const isEditMode = !!account.id

  const onSubmit = async (enterprise: UITypes.EnterpriseAccount) => {
    try {
      const formData = prepareEnterpriseFormData(enterprise)
      const res =
        isEditMode && enterprise.id
          ? await updateEnterprise({ id: enterprise.id, body: formData })
          : await createEnterprise(formData)

      const errorMessage = getErrorMessage(res.error, "description")

      if (errorMessage) {
        toast.error(
          <div>
            {errorMessage.split("\n").map((message, index) => (
              <div key={`${message}-${index}`}>{message}</div>
            ))}
          </div>,
        )
        return
      }

      toast.success(`Enterprise account has been successfully ${isEditMode ? "updated" : "added"}!`)
      dispatch(setCurrentZip(undefined))
      onSuccess()
    } catch (error) {
      toast.error("Something went wrong while creating enterprise account")
      console.error(error)
    }
  }

  const onCancelClick = () => {
    dispatch(setCurrentZip(undefined))
    onCancel()
  }

  return (
    <>
      {createPortal(
        <ModalLayout
          onClose={onCancelClick}
          closeIconClassName={styles.closeIconClassName}
          isDrawer
          isWithoutFooter
          isFetching={isCreating || isUpdating}
        >
          <ModalHeader title={isEditMode ? "Edit Enterprise" : "Add Enterprise"} borderless withShadow>
            <IconContractor />
          </ModalHeader>
          <EnterpriseAccountModalContent account={account} onSubmit={onSubmit} />
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
